/*
 * Copyright 2026 Sony Corporation
 */

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';

export interface GatewayDetailProps {
  /** Undefined until a bundle has been issued for this world. */
  gatewayId: string | undefined;
  status: 'online' | 'offline' | undefined;
  worldName: string;
  devices: Device[];
  /** Live shadows, keyed by device; absent until a gateway reports one. */
  states: Record<string, DeviceStateResponse>;
  /** When the gateway last announced itself, as the service recorded it. */
  lastSeen: string | undefined;
  onRefresh: () => void;
  refreshing: boolean;
  onDownloadConfig: () => void;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/** PTP/IP clients assume this port and it is never remapped (devplan 3.4). */
const PTP_PORT = 15740;

/** `YYYY-MM-DD HH:MM` in the reader's own timezone. */
const stamp = (iso: string | undefined): string => {
  if (!iso) {
    return '—';
  }
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return '—';
  }
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

const since = (seconds: number | undefined): string =>
  seconds === undefined ? '—' : stamp(new Date(seconds * 1000).toISOString());

const Summary = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" className="block">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

/**
 * How this world's gateway and its devices are actually doing.
 *
 * Three different kinds of "connected" live here and are deliberately kept in
 * separate columns: the gateway's own MQTT link, whether a device's twin has
 * ever been reported, and whether a PTP client is holding that device. A world
 * can be fully online with every device idle.
 */
export const GatewayDetail = ({
  gatewayId,
  status,
  worldName,
  devices,
  states,
  lastSeen,
  onRefresh,
  refreshing,
  onDownloadConfig,
}: GatewayDetailProps) => {
  const inSync = devices.filter((device) => states[device.device_id]?.state.reported).length;
  const inUse = devices.filter((device) => states[device.device_id]?.state.reported?.session.connected).length;

  if (!gatewayId) {
    // The only route to a first bundle: registering a gateway is what creates
    // the thing this page otherwise reports on.
    return (
      <Box className="absolute inset-0 z-10 overflow-auto p-6" sx={{ bgcolor: 'background.default' }}>
        <Typography variant="h6" className="mb-3">
          Gateway
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-3">
          No gateway registered yet. A gateway runs this world's devices and reports their twins.
        </Typography>
        <Button startIcon={<DownloadIcon />} onClick={onDownloadConfig} size="small" variant="outlined">
          Gateway config
        </Button>
      </Box>
    );
  }

  return (
    <Box className="absolute inset-0 z-10 overflow-auto p-6" sx={{ bgcolor: 'background.default' }}>
      <Box className="mb-4 flex items-center gap-2">
        <Typography variant="h6">{gatewayId}</Typography>
        <Chip
          size="small"
          variant="outlined"
          label={status === undefined ? 'Unknown' : status === 'online' ? 'Online' : 'Offline'}
          color={status === 'online' ? 'success' : status === 'offline' ? 'warning' : 'default'}
        />

        <Box className="flex-1" />
        <Button startIcon={<DownloadIcon />} onClick={onDownloadConfig} size="small">
          Gateway config
        </Button>
        {/* Everything here otherwise waits on the gateway's 30 s heartbeat, which
            is a long time to stare at a page after starting one. */}
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={refreshing} aria-label="Refresh">
              {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Stack direction="row" spacing={4} className="mb-4">
        <Summary label="World" value={worldName} />
        <Summary label="Devices" value={String(devices.length)} />
        <Summary label="Twins in sync" value={`${inSync} of ${devices.length}`} />
        <Summary label="Held by a client" value={String(inUse)} />
        <Summary label="Last contact" value={stamp(lastSeen)} />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Serial</TableCell>
              <TableCell>Connect to</TableCell>
              <TableCell>PTP client</TableCell>
              <TableCell>Since</TableCell>
              <TableCell>Twin</TableCell>
              <TableCell>Twin updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => {
              const state = states[device.device_id];
              const reported = state?.state.reported;
              const session = reported?.session;
              const planned = `${device.address}:${PTP_PORT}`;
              // The gateway's own answer wins: `addr` is local truth and a lab
              // whose subnet differs is expected to edit it, which would leave
              // the record naming a host no client can reach.
              const reachable = reported?.identity.address ?? planned;
              return (
                <TableRow key={device.device_id} className={reported ? undefined : 'opacity-60'}>
                  <TableCell>{device.name}</TableCell>
                  <TableCell className="font-mono">{device.model}</TableCell>
                  <TableCell className="font-mono">{device.serial}</TableCell>
                  <TableCell>
                    <Typography variant="body2" className="font-mono">
                      {reachable}
                    </Typography>
                    {reported && reachable !== planned && (
                      <Typography variant="caption" color="warning.main" className="block">
                        edited locally; this world records {planned}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{session?.connected ? (session.client ?? 'Connected') : 'Idle'}</TableCell>
                  <TableCell className="font-mono">{session?.connected ? since(session.since) : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={reported ? 'In Sync' : 'No gateway'}
                      color={reported ? 'success' : 'default'}
                    />
                  </TableCell>
                  {/* The device's own report time, from the shadow metadata. */}
                  <TableCell className="font-mono">{since(state?.reported_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {devices.length === 0 && (
        <Typography variant="body2" color="text.secondary" className="p-3">
          This world has no devices yet.
        </Typography>
      )}
    </Box>
  );
};
